import type { ComponentWithIconProps, StackProps } from '@hover/blueprint';
import {
  Body,
  Center,
  Heading,
  Icon,
  VStack,
  motion,
  system,
  transition,
  useTheme,
} from '@hover/blueprint';
import { iAlertTriangle } from '@hover/icons';
import { useMotionTemplate, useMotionValue, useSpring } from 'framer-motion';
import type { MouseEvent, MouseEventHandler, ReactNode } from 'react';
import React from 'react';

import { prefixMotionStyle } from 'src/shared/helpers/styles';

import {
  exteriorMidCenturyModernImage,
  exteriorMonochromaticImage,
} from '../Home/styles';

export interface ErrorSplashProps
  extends StackProps,
    Partial<ComponentWithIconProps> {
  heading: ReactNode;
  subtitle: ReactNode;
  callToAction?: ReactNode;
}

const localFor = ({ clientX, clientY, currentTarget }: MouseEvent) => {
  const { left, top } = currentTarget.getBoundingClientRect();

  return { x: clientX - left, y: clientY - top };
};

type LocalCoordinatesHandler = (args: {
  x: number;
  y: number;
  event: MouseEvent;
}) => void;

const localHandler =
  <T extends HTMLElement>(
    handler: LocalCoordinatesHandler,
  ): MouseEventHandler<T> =>
  event =>
    handler({ ...localFor(event), event });

const ErrorSplash = ({
  heading,
  subtitle,
  callToAction,
  icon,
  ...props
}: ErrorSplashProps) => {
  const {
    motion: {
      framer: { snappy },
    },
  } = useTheme();

  const mouseX = useMotionValue(-200);
  const mouseY = useMotionValue(-200);

  const springX = useSpring(mouseX, snappy);
  const springY = useSpring(mouseY, snappy);

  const gradient = useMotionTemplate`radial-gradient(circle closest-side at ${springX}px ${springY}px, black, transparent)`;

  return (
    <Center
      data-group
      height={{ base: 'var(--chakra-vh)', tablet: '75vh' }}
      minHeight={{ tablet: '800' }}
      position={{ base: 'fixed', tablet: 'relative' }}
      width="100%"
      zIndex={{ base: -1, tablet: 'unset' }}
      {...props}
    >
      <Center
        height="100%"
        onMouseEnter={localHandler(({ x, y }) => {
          mouseX.jump(x);
          mouseY.jump(y);
        })}
        onMouseMove={localHandler(({ x, y }) => {
          mouseX.set(x);
          mouseY.set(y);
        })}
        overflow="hidden"
        width="100%"
      >
        <Center
          height="100%"
          opacity={0.3}
          overflow="hidden"
          sx={{
            maskImage:
              'radial-gradient(ellipse farthest-side, transparent, black 300px, transparent)',
          }}
          width="100%"
          zIndex={1}
        >
          <system.div
            backgroundImage={exteriorMonochromaticImage}
            backgroundPosition="center"
            backgroundSize="cover"
            filter="saturate(0.25)"
            height="100%"
            opacity={0.5}
            width="100%"
          />
          <motion.div
            _groupHover={{ opacity: 1 }}
            backgroundImage={exteriorMidCenturyModernImage}
            backgroundPosition="center"
            backgroundSize="cover"
            filter="saturate(0.5)"
            height="100%"
            opacity={0}
            position="absolute"
            style={prefixMotionStyle({ maskImage: gradient })}
            sx={transition.easing500('opacity')}
            width="100%"
          />
        </Center>
        <VStack position="absolute" spacing="200" zIndex={2}>
          <Heading as="h1" mb="0" size={800}>
            {heading}
          </Heading>
          <Heading
            alignItems="center"
            as="h2"
            display="inline-flex"
            isLight
            size={300}
          >
            <Icon icon={icon ?? iAlertTriangle} isInline mr="100" />
            {subtitle}
          </Heading>
          {callToAction && (
            <Body lineHeight="400" maxWidth="750" textAlign="center">
              {callToAction}
            </Body>
          )}
        </VStack>
      </Center>
    </Center>
  );
};

export { ErrorSplash };
